import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRuntimeConfig } from '@/lib/runtimeConfig';
import { useAuth } from '@/auth/useAuth';
import { landingRoute } from '@/shell/navigation';
import type { Permission } from '@/auth/permissions';
import { isApiProblem } from '@/api/problems';
import { formatFacilityDate } from '@/lib/time';
import { LOCAL_PASSWORD_MIN_LENGTH } from '@/auth/localAccounts';
import { formatCount } from '@/lib/format';
import udcLogo from '../../../design-system/brand/udc-logo-web.svg';

/**
 * `/login` — W-1.1. Figure 7.3.
 *
 * One screen, two paths, and a rule that decides the project's security
 * posture at handover: the local commissioning form is present **only** while
 * `Auth:LocalSignInEnabled` is true, read at runtime from `/config.json`.
 * `Auth:LocalSignInEnabled === false` in production is an acceptance
 * criterion, not a suggestion.
 */
export default function LoginRoute() {
  const config = getRuntimeConfig();
  const { me, signInWithSso, signInWithLocal } = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'sso' | 'local' | null>(null);

  const localExpiry = me?.localAccount?.expiresAt ?? null;

  async function attempt(kind: 'sso' | 'local') {
    setFieldError(null);
    setFormError(null);

    if (kind === 'local') {
      // Checked client-side so the operator is told before the round trip;
      // the server checks it again, because the client is not the gate.
      if (password.length < LOCAL_PASSWORD_MIN_LENGTH) {
        setFieldError(
          `A commissioning password is at least ${LOCAL_PASSWORD_MIN_LENGTH} characters.`,
        );
        return;
      }
    }

    setBusy(kind);
    try {
      const session =
        kind === 'sso' ? await signInWithSso() : await signInWithLocal(username, password);
      // Navigate only once the attempt has actually succeeded. Driving this
      // off a status effect instead would send an already-signed-in visitor
      // away mid-submit, and they would never see why their attempt failed.
      //
      // The landing route is computed from the session that just came back,
      // not from the hook's `has`: this render began before the sign-in, so
      // its permissions are the ones the visitor had on the way in — none.
      const granted = new Set<Permission>((session?.permissions ?? []) as Permission[]);
      navigate(
        landingRoute((permission) => granted.has(permission)),
        { replace: true },
      );
      return;
    } catch (error) {
      setBusy(null);
      if (isApiProblem(error)) {
        // A 422 on a bad role assignment renders as a field error, not a toast:
        // the operator needs to know which input is the problem.
        if (error.is('invalid-role-assignment')) {
          setFieldError(error.detail ?? error.title);
          return;
        }
        if (error.is('local-signin-disabled')) {
          setFormError(error.detail ?? error.title);
          return;
        }
        if (error.status === 422) {
          setFieldError(error.detail ?? error.title);
          return;
        }
        setFormError(error.detail ?? error.title);
        return;
      }
      setFormError(error instanceof Error ? error.message : 'Sign-in failed.');
    }
  }

  return (
    <div className="grid grid-cols-1 gap-0 overflow-hidden pv-card shadow-[var(--pv-shadow-lg)] md:grid-cols-[1.1fr_1fr]">
      {/*
       * The facility panel. UDC's charcoal rather than a photograph until UDC
       * supplies one with usage rights (DESIGN.md §1); the faint bay rows are
       * the product's own subject, drawn, so the panel needs no image at all.
       */}
      <section className="relative flex flex-col overflow-hidden bg-pv-footer px-6 py-12 sm:px-10 text-white">
        <svg
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.07]"
          preserveAspectRatio="xMidYMid slice"
          viewBox="0 0 400 400"
        >
          <defs>
            <pattern id="pv-login-bays" width="26" height="112" patternUnits="userSpaceOnUse">
              <rect
                x="1"
                y="4"
                width="22"
                height="44"
                fill="none"
                stroke="white"
                strokeWidth="1.2"
              />
              <rect x="1" y="50" width="22" height="44" fill="white" />
            </pattern>
          </defs>
          <rect
            width="400"
            height="400"
            fill="url(#pv-login-bays)"
            transform="rotate(-12 200 200)"
          />
        </svg>
        <div className="relative flex flex-1 flex-col">
          <p className="pv-eyebrow text-pv-band-label">
            {config.facilityName} · {config.facilityLongName}
          </p>
          <h1 className="mt-3 text-4xl font-extrabold tracking-[-0.02em]">Parking Management</h1>
          <p className="pv-figure mt-10 text-7xl font-extrabold leading-none tracking-[-0.03em]">
            {formatCount(config.bayCount)}
          </p>
          <p className="mt-2 text-pv-band-label">retail parking bays monitored in real time</p>
          <div className="mt-auto border-t border-white/15 pt-5">
            <p className="font-semibold">{config.tenantName}</p>
          </div>
        </div>
      </section>

      <section className="border-t-[3px] border-pv-primary px-6 py-10 sm:px-10">
        <img
          src={udcLogo}
          alt="United Development Company logo"
          width={86}
          height={56}
          className="h-14 w-auto"
        />
        <h2 className="mt-8 text-3xl font-bold tracking-[-0.01em] text-pv-ink">Sign in</h2>
        <p className="mt-1 text-pv-muted">Use your UDC account.</p>

        <button
          type="button"
          onClick={() => void attempt('sso')}
          disabled={busy !== null}
          className="mt-6 w-full whitespace-normal pv-btn pv-btn-primary px-4 py-3.5 text-center sm:whitespace-nowrap disabled:opacity-60"
        >
          {busy === 'sso' ? 'Signing in…' : 'Continue with UDC single sign-on'}
        </button>

        {formError ? (
          <p role="alert" className="mt-3 pv-error">
            {formError}
          </p>
        ) : null}

        {/*
         * The local commissioning form exists only while the flag is true. It
         * is absent rather than disabled, so a production build has no local
         * sign-in surface at all.
         */}
        {config.authLocalSignInEnabled ? (
          <>
            <div className="my-7 flex items-center gap-4" aria-hidden="true">
              <span className="h-px flex-1 bg-pv-line" />
              <span className="text-pv-muted">or</span>
              <span className="h-px flex-1 bg-pv-line" />
            </div>

            <form
              onSubmit={(event) => {
                event.preventDefault();
                void attempt('local');
              }}
              noValidate
            >
              <h3 className="font-semibold text-pv-ink">Commissioning access</h3>

              <label htmlFor="pv-login-account" className="mt-3 block font-semibold text-pv-ink">
                Account
              </label>
              <input
                id="pv-login-account"
                name="username"
                autoComplete="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                className="mt-1 w-full pv-field px-3 py-2.5"
                placeholder="commissioning.team"
                {...(fieldError ? { 'aria-invalid': true } : {})}
              />

              <label htmlFor="pv-login-password" className="mt-3 block font-semibold text-pv-ink">
                Password
              </label>
              <input
                id="pv-login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="mt-1 w-full pv-field px-3 py-2.5"
                {...(fieldError
                  ? { 'aria-invalid': true, 'aria-describedby': 'pv-login-error' }
                  : {})}
              />

              {fieldError ? (
                <p id="pv-login-error" role="alert" className="mt-2 pv-error">
                  {fieldError}
                </p>
              ) : null}

              <div className="mt-4 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={busy !== null}
                  className="pv-btn pv-btn-secondary px-5 py-2.5 disabled:opacity-60"
                >
                  {busy === 'local' ? 'Signing in…' : 'Sign in'}
                </button>
                {/*
                 * The expiry is on the form on purpose, so nobody is surprised
                 * by it three weeks into an installation.
                 */}
                <p className="text-pv-accent-text">
                  Local accounts expire{' '}
                  {localExpiry ? formatFacilityDate(localExpiry) : 'on the handover date'}
                </p>
              </div>

              <p className="mt-4 text-dense text-pv-muted">
                A commissioning account may hold only the commissioning or system-administrator
                role, expires within 90 days, and locks out for 15 minutes after five failed
                attempts. Every sign-in is written to the audit log.
              </p>
            </form>
          </>
        ) : (
          <p className="mt-7 rounded border border-pv-line bg-pv-canvas px-4 py-3 text-pv-muted">
            Local sign-in is disabled on this environment. Use UDC single sign-on, or ask UDC IT for
            an account.
          </p>
        )}
      </section>
    </div>
  );
}
